import type { Article } from 'packages/swf-help/center/src/types';
import type { ChangeEvent, FormEvent, KeyboardEventHandler } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import SearchIcon from '@swf/core/assets/icons/icon-search.svg';
import useRequest from '@swf/core/hooks/useRequest';

import { TXTCLI_Search, TXTWCL_Search_for_help } from './Reference.messages.json';
import styles from './Reference.scss';

export interface Props {
  searchTerm: string;
  setSearchTerm(value: string): void;
  showArticleList: boolean;
  setShowArticleList(flag: boolean): void;
  searchResults: Article[];
  setSearchResults(article: Article[]): void;
  referenceElement: HTMLElement | HTMLFormElement | null;
  setReferenceElement(element: HTMLDivElement | HTMLFormElement | HTMLInputElement | null): void;
  selected?: number;
  setSelected: React.Dispatch<React.SetStateAction<number | undefined>>;
  onClear(): void;
}

const Reference: React.FC<Props> = ({
  searchTerm,
  setSearchTerm,
  setShowArticleList,
  searchResults,
  setSearchResults,
  setReferenceElement,
  selected,
  setSelected,
  onClear
}) => {
  const { push } = useHistory();
  const { formatMessage } = useIntl();
  const [inputElement] = useState<HTMLInputElement | null>(null);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (selected !== undefined) {
      push(`/help/articles/${encodeURIComponent(searchResults[selected].ref)}/`);
    } else if (searchTerm) {
      push(`/help/search/${encodeURIComponent(searchTerm)}/`);
    }
    setSearchTerm('');
    onClear();
  };

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
      setShowArticleList(true);
    },
    [setSearchTerm, setShowArticleList]
  );

  const request = useRequest();

  useEffect(() => {
    if (searchTerm.length < 2) {
      setShowArticleList(false);
    } else {
      request({
        service: `help.searchArticlesByTerm`,
        body: { searchTerm }
      }).subscribe(({ body: { articles } }) => {
        setSearchResults(articles);
        setShowArticleList(true);
      });
    }
  }, [searchTerm, request, setShowArticleList, setSearchResults]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    ({ key }) => {
      const max = searchResults?.length ?? 1;
      switch (key) {
        case 'ArrowDown':
          setSelected((prevValue) => (prevValue !== undefined ? (prevValue + 1) % max : 0));
          break;
        case 'ArrowUp':
          setSelected((prevValue) => (prevValue ? (prevValue - 1) % max : max - 1));
          break;
        case 'Escape':
          inputElement?.blur();
          onClear();
          break;
        case 'Enter':
          break;
      }
    },
    [searchResults?.length, setSelected, inputElement, onClear]
  );

  return (
    <form onSubmit={handleSearch} className={styles.searchForm}>
      <div className={styles.search} ref={setReferenceElement}>
        <input
          type="text"
          id="searchArticleInput"
          className={styles.searchBarInput}
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={formatMessage(TXTWCL_Search_for_help)}
          autoCorrect="false"
          spellCheck="false"
          maxLength={2048}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          onFocus={(e) => setSearchTerm(e.target.defaultValue)}
          data-testid="search-input"
        />
        <StarsButton
          className={styles.searchBarBtn}
          type="submit"
          icon={<SearchIcon width={17} height={18} />}
          translation={TXTCLI_Search}
          data-testid="search"
        />
      </div>
    </form>
  );
};

export default Reference;
